<script lang="ts">
    import { useLocaleContext, usePropertiesContext } from '$lib/context';
    import type { ApiSchemas } from '$lib/api/shopwareApi';
    import { t } from 'svelte-i18n';
    import { goto } from '$app/navigation';

    const properties = usePropertiesContext();
    const lang = useLocaleContext();

    let classes = '';
    export { classes as class };

    export let variants: ApiSchemas['Product'][];
    export let productVariant: ApiSchemas['Product'];

    function getValidVariants() {
        return variants.map((variant) => {
            const options = variant.options || [];

            const yearOption = options.find((opt: any) => opt.groupId === properties.year.id);
            const sizeOption = options.find((opt: any) => opt.groupId === properties.size.id);

            return {
                productNumber: variant.productNumber,
                year: yearOption?.translated?.name || 'Unknown',
                size: sizeOption?.translated?.name || 'Unknown'
            };
        });
    }

    let validVariants = getValidVariants();

    let allYears: string[];
    let allSizes: string[];

    let selectedYear: string | null = null;
    let selectedSize: string | null = null;

    $: if (productVariant) {
        validVariants = getValidVariants();

        allYears = [...new Set(validVariants.map((v) => v.year))];
        allSizes = [...new Set(validVariants.map((v) => v.size))];

        const selectedVariant = validVariants.find((variant) => variant.productNumber === productVariant.productNumber);
        selectedYear = selectedVariant?.year;
        selectedSize = selectedVariant?.size;
    }

    function getValidSizesForYear(year: string): string[] {
        return validVariants.filter((v) => v.year === year).map((v) => v.size);
    }

    function getValidYearsForSize(size: string): string[] {
        return validVariants.filter((v) => v.size === size).map((v) => v.year);
    }

    function selectOption(group: 'year' | 'size', value: string) {
        if (group === 'year') {
            selectedYear = value;

            const validSizes = getValidSizesForYear(value);
            if (!validSizes.includes(selectedSize!)) {
                // invalidate selected size if it's no longer valid
                selectedSize = validSizes.length > 0 ? validSizes[0] : null;
            }
        } else if (group === 'size') {
            selectedSize = value;

            const validYears = getValidYearsForSize(value);
            if (!validYears.includes(selectedYear!)) {
                // invalidate selected year if it's no longer valid
                selectedYear = validYears.length > 0 ? validYears[0] : null;
            }
        }

        goto(
            `/${lang}/product/${validVariants.find((variant) => variant.year === selectedYear && variant.size === selectedSize)?.productNumber}`,
            { noScroll: true, replaceState: true }
        );
    }
</script>

<div class={classes}>
    <div class="flex flex-wrap gap-x-8 gap-y-5 text-neutral-500">
        {#if !allYears.find((year) => (year ?? 'unknown').length > 4)}
            <div class="flex-col flex gap-1">
                {$t('shop.product.year')}
                <div class="flex w-full flex-wrap text-primary gap-3">
                    {#each allYears as year}
                        <button
                            on:click={() => selectOption('year', year)}
                            class="px-5 py-1.5 border-primary border {year === selectedYear &&
                                'bg-primary text-neutral-50'}">{year}</button
                        >
                    {/each}
                </div>
            </div>
        {/if}
        <div class="flex flex-col gap-1">
            {$t('shop.product.size')}
            <div class="flex text-primary gap-3">
                {#each allSizes as size}
                    <button
                        on:click={() => selectOption('size', size)}
                        class="px-5 py-1.5 border-primary border {size === selectedSize &&
                            'bg-primary text-neutral-50'}">{size}</button
                    >
                {/each}
            </div>
        </div>
    </div>
</div>
